import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';

import { Workflow } from '../../../shared/models/site-content/workflow.model';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { FormInstance } from '../../../shared/models/site-content/form-instance.model';

// Define a chart type info class.
export interface IResponseManagementReportTypeInfo {
    reportType: string;
    reportId: number; // Needs to be unique values starting with 1/one, incrementing by one.

    packageDataFunction: ((metrics: any) => any);

    serviceMethodName: string;

    workflowRequired?: boolean;
    magicFolderRequired?: boolean;
    magicFormInstanceRequired?: boolean;
}
export class ResponseManagementReportTypeInfo {
    // Properties.
    public reportType: string;
    public reportId: number; // Needs to be unique values starting with 1/one, incrementing by one.

    public packageDataFunction: ((metrics: any) => any);

    public serviceMethodName: string;

    public workflowRequired: boolean = false;
    public magicFolderRequired: boolean = false;
    public magicFormInstanceRequired: boolean = false;

    // Constructor.
    public constructor({
        reportType,
        reportId,

        packageDataFunction,

        serviceMethodName,

        workflowRequired = false,
        magicFolderRequired = false,
        magicFormInstanceRequired = false
    }: IResponseManagementReportTypeInfo) {
        this.reportType = reportType;
        this.reportId = reportId;

        this.packageDataFunction = packageDataFunction;

        this.serviceMethodName = serviceMethodName;

        this.workflowRequired = workflowRequired;
        this.magicFolderRequired = magicFolderRequired;
        this.magicFormInstanceRequired = magicFormInstanceRequired;
    }
}
export class ChartTypeInfo {
    public chartType: string;
    public chartId: number; // Needs to be unique values starting with 1/one, incrementing by one.

    public packageDataFunction: (() => any);
};
export class ResponseManagementSiteModels {
    public siteWorkflows: Workflow[] = null;
    public magicFolders: Folder[] = null;
    public magicFormInstances: FormInstance[] = null;
}

@Component({
    selector: 'app-response-management-config',
    templateUrl: './response-mgmt-config.component.html',
    styleUrls: ['./response-mgmt-config.component.scss'],
    standalone: false
})
export class ResponseManagementConfigComponent implements OnInit {
    // Properties.
    // Inputs.
    @Input() reportTypes: ResponseManagementReportTypeInfo[] = [];
    @Input() chartTypes: ChartTypeInfo[] = [];

    // Outputs.
    @Output() getSiteWorkflows: EventEmitter<ResponseManagementSiteModels> = new EventEmitter<ResponseManagementSiteModels>();
    @Output() getMagicFolders: EventEmitter<ResponseManagementSiteModels> = new EventEmitter<ResponseManagementSiteModels>();
    @Output() getMagicFormInstances: EventEmitter<ResponseManagementSiteModels> = new EventEmitter<ResponseManagementSiteModels>();

    @Output() reportTypeSelectionChanged: EventEmitter<any> = new EventEmitter();
    @Output() chartTypeSelectionChanged: EventEmitter<any> = new EventEmitter();

    @Output() workflowSelectionChanged: EventEmitter<any> = new EventEmitter();
    @Output() magicFolderSelectionChanged: EventEmitter<any> = new EventEmitter();
    @Output() magicFormInstanceSelectionChanged: EventEmitter<any> = new EventEmitter();

    // Private properties.
    private iSelectedReportTypeId: number = 0;
    private iSelectedChartId: number = 0;
    private iSelectedWorkflowId: number = 0;
    private iSelectedMagicFolderId: number = 0;
    private iSelectedMagicFormInstanceId: number = 0;

    /*
    private siteWorkflows: Workflow[] = null;
    private magicFolders: Folder[] = null;
    private magicFormInstances: FormInstance[] = null;
    */
    private additionalObjects: ResponseManagementSiteModels = new ResponseManagementSiteModels();

    // Constructor.
    public constructor() {
        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {

        return;
    }

    // 'Accessor' methods called by my HTML code.
    public get ReportTypes(): ResponseManagementReportTypeInfo[] {
        return (this.reportTypes);
    }
    public get SelectedReportTypeId(): number {
        return (this.iSelectedReportTypeId);
    }
    public set SelectedReportTypeId(reportTypeIdParam: number) {
        this.iSelectedReportTypeId = reportTypeIdParam;
    }

    public get ChartTypes(): ChartTypeInfo[] {
        return (this.chartTypes);
    }
    public get SelectedChartId(): number {
        return (this.iSelectedChartId)
    }
    public set SelectedChartId(selectedChartId: number) {
        this.iSelectedChartId = selectedChartId;
    }

    public get ShowSiteWorkflows(): boolean {
        let bShow: boolean =
            ((this.iSelectedReportTypeId > 0) && (this.reportTypes[this.iSelectedReportTypeId - 1].workflowRequired));
        return (bShow);
    }
    public get SiteWorkflows(): Workflow[] {
        return (this.additionalObjects.siteWorkflows);
    }
    public get ShowMagicFolders(): boolean {
        let bShow: boolean =
            ((this.iSelectedReportTypeId > 0) && (this.reportTypes[this.iSelectedReportTypeId - 1].magicFolderRequired));
        return (bShow);
    }
    public get MagicFolders(): Folder[] {
        return (this.additionalObjects.magicFolders);
    }
    public get ShowMagicFormInstances(): boolean {
        let bShow: boolean =
            ((this.iSelectedReportTypeId > 0) && (this.reportTypes[this.iSelectedReportTypeId - 1].magicFormInstanceRequired));
        return (bShow);
    }
    public get MagicFormInstances(): FormInstance[] {
        return (this.additionalObjects.magicFormInstances);
    }

    public get SelectedWorkflowId(): number {
        return (this.iSelectedWorkflowId);
    }
    public set SelectedWorkflowId(selectedWorkflowId: number) {
        this.iSelectedWorkflowId = selectedWorkflowId;
    }
    public get SelectedMagicFolderId(): number {
        return (this.iSelectedMagicFolderId);
    }
    public set SelectedMagicFolderId(selectedFolderId: number) {
        this.iSelectedMagicFolderId = selectedFolderId;
    }
    public get SelectedMagicFormInstanceId(): number {
        return (this.iSelectedMagicFormInstanceId);
    }
    public set SelectedMagicFormInstanceId(selectedFormInstanceId: number) {
        this.iSelectedMagicFormInstanceId = selectedFormInstanceId;
    }

    // Handle control events.
    // Report configuration changes.
    public onReportTypeSelectionChanged(eventData: MatSelectChange): void {
        if (eventData.value > 0) {
            this.reportTypeSelectionChanged.emit(eventData);

            let reportTypeInfo: ResponseManagementReportTypeInfo = this.reportTypes[eventData.value - 1];
            if ((reportTypeInfo.workflowRequired) && (this.additionalObjects.siteWorkflows == null)) {
                this.getSiteWorkflows.emit(this.additionalObjects);
            } else if ((reportTypeInfo.magicFolderRequired) && (this.additionalObjects.magicFolders == null)) {
                this.getMagicFolders.emit(this.additionalObjects);
            } else if ((reportTypeInfo.magicFormInstanceRequired) && (this.additionalObjects.magicFormInstances == null)) {
                this.getMagicFormInstances.emit(this.additionalObjects);
            }
        }

        return;
    }
    public onChartTypeSelectionChanged(eventData: MatSelectChange): void {
        if (eventData.value > 0) {
            this.chartTypeSelectionChanged.emit(eventData);
        }

        return;
    }

    // Additional report object selection changes.
    public onWorkflowSelectionChanged(eventData: MatSelectChange): void {
        if (eventData.value > 0) {
            this.workflowSelectionChanged.emit(eventData);
        }

        return;
    }
    public onMagicFolderSelectionChanged(eventData: MatSelectChange): void {
        if (eventData.value > 0) {
            this.magicFolderSelectionChanged.emit(eventData);
        }

        return;
    }
    public onMagicFormInstanceSelectionChanged(eventData: MatSelectChange): void {
        if (eventData.value > 0) {
            this.magicFormInstanceSelectionChanged.emit(eventData);
        }

        return;
    }
}
